import { useState, type FormEvent, type ReactNode } from 'react';

import { AuthError } from '../auth/cognito.js';
import { useSession } from '../session.js';

/**
 * Sign-up, verification, sign-in and password reset (M1.14.2).
 *
 * One component with four states rather than four routes. Every path through
 * it is a continuation of the same attempt — sign up and you land on the code
 * form; sign in unconfirmed and you land on the *same* code form — and routes
 * would mean carrying the email address through the URL, where it does not
 * belong.
 *
 * ## Errors are rendered from the failure, never from the raw message
 *
 * Cognito says "Incorrect username or password." for a wrong password, an
 * unknown account, and a mistyped email alike. Repeating that verbatim tells a
 * user nothing they can act on. Each `kind` gets wording written for the
 * person reading it — and "no such account" is deliberately *not* one of them,
 * because distinguishing it turns this form into a way to discover which email
 * addresses have accounts here.
 */

type Mode = 'sign-in' | 'sign-up' | 'confirm' | 'forgot' | 'reset';

const MESSAGES: Record<string, string> = {
  'invalid-credentials': 'That email and password do not match an account.',
  unconfirmed: 'This account still needs verifying. We have sent a new code.',
  'user-exists': 'There is already an account with that address. Try signing in.',
  'invalid-code': 'That code is not right. Check it and try again.',
  'expired-code': 'That code has expired. Ask for a new one.',
  'weak-password': 'Passwords need at least twelve characters.',
  'too-many-attempts': 'Too many attempts. Wait a few minutes and try again.',
  network: 'We could not reach the service. Check your connection.',
  unknown: 'Something went wrong. Try again.',
};

export function AuthScreens(): ReactNode {
  const session = useSession();
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const run = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await work();
    } catch (caught) {
      const failure = caught instanceof AuthError ? caught : undefined;
      setError(MESSAGES[failure?.kind ?? 'unknown'] ?? MESSAGES['unknown']);

      // An unconfirmed account is not really an error — it is the next step.
      if (failure?.kind === 'unconfirmed') {
        await session.auth.resendCode(email).catch(() => undefined);
        setMode('confirm');
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();

    switch (mode) {
      case 'sign-in':
        void run(async () => {
          await session.signIn(email, password);
        });
        return;
      case 'sign-up':
        void run(async () => {
          const result = await session.auth.signUp(email, password);
          setNotice(
            result.confirmed
              ? 'Account created. Sign in to continue.'
              : 'Check your email for a six-digit code.',
          );
          setMode(result.confirmed ? 'sign-in' : 'confirm');
        });
        return;
      case 'confirm':
        void run(async () => {
          await session.auth.confirmSignUp(email, code);
          // Straight in: the password is still in the form, and asking
          // somebody to type it again immediately is pure friction.
          await session.signIn(email, password);
        });
        return;
      case 'forgot':
        void run(async () => {
          await session.auth.forgotPassword(email);
          setNotice('If that address has an account, a reset code is on its way.');
          setMode('reset');
        });
        return;
      case 'reset':
        void run(async () => {
          await session.auth.confirmForgotPassword(email, code, password);
          setNotice('Password changed.');
          await session.signIn(email, password);
        });
        return;
    }
  };

  return (
    <main className="auth">
      <div className="auth__card">
        <h1 className="auth__title">Feed Reader</h1>
        <p className="auth__subtitle">{subtitleFor(mode)}</p>

        <form onSubmit={submit} noValidate>
          {mode !== 'confirm' && mode !== 'reset' ? (
            <Field
              label="Email"
              type="email"
              value={email}
              autoComplete="email"
              onChange={setEmail}
            />
          ) : (
            <p className="auth__for">
              for <strong>{email}</strong>
            </p>
          )}

          {(mode === 'confirm' || mode === 'reset') && (
            <Field
              label="Verification code"
              type="text"
              value={code}
              autoComplete="one-time-code"
              inputMode="numeric"
              onChange={setCode}
            />
          )}

          {mode !== 'forgot' && mode !== 'confirm' && (
            <Field
              label={mode === 'reset' ? 'New password' : 'Password'}
              type="password"
              value={password}
              autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
              onChange={setPassword}
              hint={mode === 'sign-in' ? undefined : 'At least twelve characters.'}
            />
          )}

          {error !== undefined && (
            <p className="auth__error" role="alert">
              {error}
            </p>
          )}
          {error === undefined && notice !== undefined && (
            <p className="auth__notice">{notice}</p>
          )}

          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'Working…' : actionFor(mode)}
          </button>
        </form>

        <div className="auth__links">
          {mode === 'sign-in' && (
            <>
              <button className="link" onClick={() => setMode('sign-up')}>
                Create an account
              </button>
              <button className="link" onClick={() => setMode('forgot')}>
                Forgotten password
              </button>
            </>
          )}
          {mode !== 'sign-in' && (
            <button className="link" onClick={() => setMode('sign-in')}>
              Back to sign in
            </button>
          )}
          {mode === 'confirm' && (
            <button
              className="link"
              onClick={() => {
                void session.auth.resendCode(email);
                setNotice('A new code is on its way.');
              }}
            >
              Send another code
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  inputMode,
  hint,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  inputMode?: 'numeric';
  hint?: string | undefined;
}): ReactNode {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        className="field__input"
        type={type}
        value={value}
        autoComplete={autoComplete}
        {...(inputMode === undefined ? {} : { inputMode })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        required
      />
      {hint !== undefined && <span className="field__hint">{hint}</span>}
    </label>
  );
}

const subtitleFor = (mode: Mode): string => {
  switch (mode) {
    case 'sign-in':
      return 'Sign in to your feeds.';
    case 'sign-up':
      return 'A reader with a bottom you can reach.';
    case 'confirm':
      return 'Enter the code we emailed you.';
    case 'forgot':
      return 'We will send you a reset code.';
    case 'reset':
      return 'Choose a new password.';
  }
};

const actionFor = (mode: Mode): string => {
  switch (mode) {
    case 'sign-in':
      return 'Sign in';
    case 'sign-up':
      return 'Create account';
    case 'confirm':
      return 'Verify';
    case 'forgot':
      return 'Send code';
    case 'reset':
      return 'Change password';
  }
};
